import { useEffect, useMemo, useState } from 'react';
import {
  Users, Plus, Loader2, UserPlus, UserCog, Send, ShieldOff, Link2,
} from 'lucide-react';
import { INVITE_LINK_COPY_EN, teamInviteOpen } from '@/lib/inviteLink';
import { useInviteLinkDialog } from '@/components/ui/InviteLinkDialog';
import {
  fetchAdminUsers, fetchAdminUser,
  inviteAdminUser, updateAdminUser, resendAdminUserInvite, copyAdminUserInviteLink, resetUserMfa,
  getDisplayErrorMessage, getErrorCode,
  fetchRoles, fetchLocations, fetchChargers, fetchFleets,
  type UserView, type UserDetailView, type UserState,
  type RoleView, type FleetSummary,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { isSalesTemplate, matrixToPermissionStrings } from '@evi/shared';
import { groupRoles, roleDisplayName, roleNameForDisplay } from './roleLabels.js';
import {
  SALES_TEAM_PICKS, salesTeamOptionValue, salesTeamPickFromValue, saveSalesStaffPrefill, type SalesTeamPickOption,
} from '@/lib/salesStaffPrefill';
import { Modal } from '@/components/ui/Modal';
import {
  ScopePicker, type ScopePickerLocation, type ScopePickerCharger,
} from '@/components/access/ScopePicker';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './UsersPage.module.css';
import { Switch } from '@/components/ui/Switch';

/** The string Role:read emits. Derived from the shared resource map,
 *  not written out, so it cannot drift from the server's gate. */
const ROLE_READ_PERMISSION: string = matrixToPermissionStrings([{
  resource: 'Role', create: false, read: true, update: false, delete: false,
}] as never)[0] ?? 'role:read';

/**
 * Phase 2.7 — Admin Users (team management).
 *
 * Lists every NON-DRIVER user in the caller's tenant (the team
 * with role assignments). Invite + edit run through a shared
 * modal driven by `mode`.
 *
 * Driver-tier surfaces (locked-out drivers, RFID assignments,
 * gamification leaderboard) live elsewhere — this page is
 * intentionally just the operator/admin/viewer/custom-role team.
 */

const STATE_LABELS: Record<UserState, string> = {
  pending: 'Pending invite',
  active: 'Active',
  expired: 'Expired invite',
  deactivated: 'Deactivated',
};

const STATE_CLASSES: Record<UserState, string> = {
  pending: styles.statePending!,
  active: styles.stateActive!,
  expired: styles.stateExpired!,
  deactivated: styles.stateDeactivated!,
};

type StateFilter = 'all' | UserState;

interface ModalState {
  mode: 'invite' | 'edit';
  userId?: string;
}

export function UsersPage() {
  useRecentRoute('Team');
  const [users, setUsers] = useState<UserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [modal, setModal] = useState<ModalState | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const inviteLink = useInviteLinkDialog();
  // MFA reset (super_admin only). resetConfirm holds the row awaiting
  // confirmation; resettingId drives the in-flight button state;
  // resetNotice mirrors resendNotice's inline success banner.
  const [resetConfirm, setResetConfirm] = useState<UserView | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  // The reset action mirrors the endpoint's super_admin gate. The
  // endpoint is the real boundary; this only decides UI visibility.
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  // Team management is gated on Role:read, derived from the shared
  // resource map so it cannot drift from the server's gate. super_admin
  // passes by bypass, exactly as requireMatrixPermission does, because
  // its derived string set is incomplete by design.
  const canManageTeam = isSuperAdmin
    || (currentUser?.permissions ?? []).includes(ROLE_READ_PERMISSION);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      setUsers(await fetchAdminUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleResend = async (user: UserView): Promise<void> => {
    setResendingId(user._id);
    setResendNotice(null);
    setError('');
    try {
      const updated = await resendAdminUserInvite(user._id);
      // Optimistic row patch — keeps the list responsive without a
      // full refetch. Refetch happens implicitly next time the page
      // re-mounts.
      setUsers((prev) => prev.map((u) => u._id === updated._id
        ? {
            ...u,
            state: updated.state,
            invite_expires_at: updated.invite_expires_at,
          }
        : u,
      ));
      setResendNotice(`Invite re-sent to ${user.email}.`);
    } catch (err) {
      // Race: the invitee signed in between the list load and this click, so
      // the server rejects with USER_ALREADY_ACTIVE. Resolve it gracefully —
      // flip the row to active (which hides Re-send) and show an informational
      // notice instead of a scary error.
      if (await getErrorCode(err) === 'USER_ALREADY_ACTIVE') {
        setUsers((prev) => prev.map((u) => (u._id === user._id
          ? { ...u, state: 'active', last_login: u.last_login ?? new Date().toISOString() }
          : u)));
        setResendNotice(`${user.email} has already signed in — no invite needed.`);
      } else {
        setError(await getDisplayErrorMessage(err, 'Failed to re-send invite'));
      }
    } finally {
      setResendingId(null);
    }
  };

  const handleResetMfa = async (user: UserView): Promise<void> => {
    setResettingId(user._id);
    setResetNotice(null);
    setError('');
    try {
      await resetUserMfa(user._id);
      // Optimistic flip — the badge reads off mfa_enabled, so clearing
      // it on the row reflects the reset without a full refetch.
      setUsers((prev) => prev.map((u) => u._id === user._id
        ? { ...u, mfa_enabled: false }
        : u,
      ));
      setResetNotice(`MFA reset for ${user.email}. They'll set it up again at next sign-in.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset MFA');
    } finally {
      setResettingId(null);
      setResetConfirm(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (stateFilter !== 'all' && u.state !== stateFilter) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q)
        || (u.role_name ? roleNameForDisplay(u.role_name).toLowerCase().includes(q) : false)
      );
    });
  }, [users, search, stateFilter]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Users size={18} /></div>
          <div>
            <h1 className={styles.title}>Team</h1>
            <p className={styles.subtitle}>
              {users.length} teammate{users.length === 1 ? '' : 's'}
              {users.length > 0 && (
                <>
                  {' · '}
                  {users.filter((u) => u.state === 'pending').length} pending
                  {' · '}
                  {users.filter((u) => u.state === 'deactivated').length} deactivated
                </>
              )}
            </p>
          </div>
        </div>
        {/* Managing the team needs Role:read — the modal's first act is
            fetchRoles(), and the invite route requires it server-side.
            The operator tier holds User:read (so the roster is theirs to
            see) but deliberately not Role:read, per Warren's 2026-09-24
            ruling: an operator does day-to-day work, a tenant admin owns
            the team. Without this the button opened a modal that 403'd
            before it rendered a field. */}
        {canManageTeam ? (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setModal({ mode: 'invite' })}
          >
            <Plus size={15} /> Invite teammate
          </button>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--color-tx-m)', maxWidth: 220, textAlign: 'right', lineHeight: 1.4 }}>
            Your tenant admin adds and edits teammates.
          </span>
        )}
      </div>

      <div className={styles.filterRow}>
        <input
          className={styles.search}
          type="text"
          placeholder="Search by name, email, or role"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={styles.select}
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as StateFilter)}
          aria-label="Filter by state"
        >
          <option value="all">All states</option>
          <option value="active">Active</option>
          <option value="pending">Pending invite</option>
          <option value="expired">Expired invite</option>
          <option value="deactivated">Deactivated</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {resendNotice && <div className={styles.successNotice}>{resendNotice}</div>}
      {resetNotice && <div className={styles.successNotice}>{resetNotice}</div>}

      {loading ? (
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.emptyState}>
          {search || stateFilter !== 'all'
            ? 'No teammates match those filters.'
            : 'No teammates yet. Click "Invite teammate" to add the first one.'}
        </div>
      ) : (
        <div className={styles.list}>
          {filtered.map((u) => {
            // Only outstanding invites can be re-sent. `!u.last_login` is the
            // defensive guard: a teammate who has EVER signed in is active and
            // must never show Re-send, even if their derived state is stale in
            // a list left open across a sign-in.
            // On what the Api says this viewer may do (the row's
            // can_resend_invite: the resend's gate, Role:read + User:create,
            // and never a teammate above the viewer's tier), so an operator
            // or a lower tier never sees a button that 403s (review finding
            // B). Copy invite link shows on exactly this rule too (29 October
            // 2026, lib/inviteLink.ts): its endpoint runs the resend's gate.
            const canResend = teamInviteOpen(u);
            return (
              <div
                key={u._id}
                // Without Role:read the edit modal 403s on its own first
                // fetch, so for those callers the row is a row, not a
                // button — including for keyboard users, who would
                // otherwise tab onto something that opens and fails.
                {...(canManageTeam
                  ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    onClick: () => setModal({ mode: 'edit', userId: u._id }),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setModal({ mode: 'edit', userId: u._id });
                      }
                    },
                  }
                  : {})}
                className={styles.row}
              >
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>{u.name}</span>
                  <span className={styles.rowEmail}>{u.email}</span>
                </div>
                <div className={styles.rowRole}>
                  {u.role_name ? roleNameForDisplay(u.role_name) : <em>No role assigned</em>}
                </div>
                <span className={`${styles.stateBadge} ${STATE_CLASSES[u.state]}`}>
                  {STATE_LABELS[u.state]}
                </span>
                <span
                  className={`${styles.mfaBadge} ${u.mfa_enabled ? styles.mfaOn : styles.mfaOff}`}
                  title={u.mfa_enabled ? 'Two-factor authentication enabled' : 'No two-factor authentication'}
                >
                  {u.mfa_enabled ? '2FA on' : '2FA off'}
                </span>
                <span className={styles.rowAside}>
                  {u.state === 'active' && u.last_login
                    ? `Last seen ${formatRelative(u.last_login)}`
                    : u.state === 'pending' && u.invite_expires_at
                      ? `Expires ${formatRelative(u.invite_expires_at)}`
                      : u.state === 'expired'
                        ? 'Invite expired'
                        : ''}
                </span>
                {/* Reset MFA — super_admin only, mirroring the
                    endpoint. Matches the Re-send pattern verbatim:
                    stopPropagation so it doesn't open the edit modal,
                    in-flight state keyed on resettingId. Opens a
                    confirm rather than firing immediately. Only shown
                    when the target actually has MFA armed. */}
                {isSuperAdmin && u.mfa_enabled && (
                  <button
                    type="button"
                    className={styles.resetMfaBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setResetConfirm(u);
                    }}
                    disabled={resettingId === u._id}
                    aria-label={`Reset MFA for ${u.email}`}
                  >
                    {resettingId === u._id
                      ? <><Loader2 size={12} className={styles.spin} /> Resetting…</>
                      : <><ShieldOff size={12} /> Reset MFA</>}
                  </button>
                )}
                {canResend && (
                  <span className={styles.inviteActions}>
                    <button
                      type="button"
                      className={styles.resendBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleResend(u);
                      }}
                      disabled={resendingId === u._id}
                      aria-label={`Re-send invite to ${u.email}`}
                    >
                      {resendingId === u._id
                        ? <><Loader2 size={12} className={styles.spin} /> Sending…</>
                        : <><Send size={12} /> Re-send</>}
                    </button>
                    <button
                      type="button"
                      className={styles.resendBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        void inviteLink.open({ email: u.email, name: u.name }, () => copyAdminUserInviteLink(u._id));
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      disabled={inviteLink.busyFor === u.email}
                      aria-label={`${INVITE_LINK_COPY_EN.action} for ${u.email}`}
                    >
                      {inviteLink.busyFor === u.email
                        ? <Loader2 size={12} className={styles.spin} />
                        : <Link2 size={12} />} {INVITE_LINK_COPY_EN.action}
                    </button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Copy invite link's dialog, outside the rows so its clicks never open the edit modal. */}
      {inviteLink.dialog}

      {modal && (
        <UserModal
          mode={modal.mode}
          userId={modal.userId}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void load();
          }}
        />
      )}

      {/* Reset-MFA confirm — the logout-confirm idiom: small Modal,
          explicit body copy naming the next-sign-in re-enrollment AND
          the live-session caveat, Cancel / Confirm reset footer. Opens
          from the list row, so there's no nested-modal concern. */}
      {resetConfirm && (
        <Modal
          isOpen
          onClose={() => setResetConfirm(null)}
          title="Reset two-factor authentication?"
          size="sm"
          footer={
            <>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setResetConfirm(null)}
                disabled={resettingId === resetConfirm._id}
              >
                Cancel
              </button>
              <button
                type="button"
                className={formStyles.btnPrimary}
                onClick={() => { void handleResetMfa(resetConfirm); }}
                disabled={resettingId === resetConfirm._id}
              >
                {resettingId === resetConfirm._id ? 'Resetting…' : 'Confirm reset'}
              </button>
            </>
          }
        >
          <p style={{ margin: 0, color: 'var(--color-tx)' }}>
            {resetConfirm.name} will set up two-factor authentication again the
            next time they sign in. Their current session stays active until it
            expires.
          </p>
        </Modal>
      )}
    </div>
  );
}

// ── Invite / Edit modal (shared) ───────────────────────────────────

interface UserModalProps {
  mode: 'invite' | 'edit';
  userId?: string;
  onClose: () => void;
  onSaved: () => void;
}

function UserModal({ mode, userId, onClose, onSaved }: UserModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [roles, setRoles] = useState<RoleView[]>([]);
  const [detail, setDetail] = useState<UserDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Native-admin escape hatch (super_admin only). Default is the grant path:
  // a new admin also gets a driver identity. A super_admin can opt an invite
  // into a native, driver-less admin account.
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'super_admin');
  const [native, setNative] = useState(false);

  // Sales team picks (Warren, 2026-10-20) never save here: the Sales Staff
  // page records the role with the manager and the dated start. Picking one
  // opens its add form prefilled; for a new email that form sends the invite.
  // Super Admin only, like that page.
  const [salesHandoff, setSalesHandoff] = useState('');
  const [salesChecking, setSalesChecking] = useState(false);
  const handOffToSalesStaff = async (pick: SalesTeamPickOption): Promise<void> => {
    setSalesHandoff('');
    const address = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setSalesHandoff('Enter their email first. The Sales Staff page finds the person by it.');
      return;
    }
    // A new email is handed over too: the Sales Staff form sends the normal account invite and the record is
    // created, with the manager and start date entered there, when they accept.
    setSalesChecking(true);
    if (!saveSalesStaffPrefill({ email: address, name: name.trim(), pick: pick.pick, role: pick.role })) {
      setSalesChecking(false);
      setSalesHandoff('This browser would not hold the details for the Sales Staff page. Open Sales Staff and add them there.');
      return;
    }
    window.location.assign('/sales-staff');
  };

  // Scope state — used only on invite mode when the picked role
  // is a third_party_admin. Available locations/chargers fetched
  // alongside roles; selection state is local to the modal until
  // submit. The lazy-fetch pattern (only when needed) keeps the
  // modal lightweight for the common operator/viewer invite case
  // where scope is irrelevant.
  const [scopeLocations, setScopeLocations] = useState<ScopePickerLocation[]>([]);
  const [scopeChargers, setScopeChargers] = useState<ScopePickerCharger[]>([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeLoaded, setScopeLoaded] = useState(false);
  const [draftLocations, setDraftLocations] = useState<Set<string>>(new Set());
  const [draftChargers, setDraftChargers] = useState<Set<string>>(new Set());

  // Fleet state — invite mode, fleet_admin role only. A fleet admin is bound
  // to fleets through AccessAssignment.fleet_ids and the backend REQUIRES at
  // least one at invite time (400 FLEET_REQUIRED), because an unbound fleet
  // admin sees no fleets and cannot be repaired from the fleet side. Same
  // lazy-fetch shape as scope: fetched the first time the role makes it
  // relevant, reused across role-picker tweaks within the modal session.
  const [fleets, setFleets] = useState<FleetSummary[]>([]);
  const [fleetsLoading, setFleetsLoading] = useState(false);
  const [fleetsLoaded, setFleetsLoaded] = useState(false);
  const [draftFleets, setDraftFleets] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async (): Promise<void> => {
      try {
        const rolesPromise = fetchRoles();
        if (mode === 'edit' && userId) {
          const [allRoles, userDetail] = await Promise.all([rolesPromise, fetchAdminUser(userId)]);
          if (cancelled) return;
          setRoles(allRoles);
          setDetail(userDetail);
          setName(userDetail.name);
          setEmail(userDetail.email);
          setRoleId(userDetail.role_id ?? '');
          setIsActive(userDetail.is_active);
        } else {
          const allRoles = await rolesPromise;
          if (cancelled) return;
          setRoles(allRoles);
          // NO DEFAULT, deliberately.
          //
          // This used to pick `allRoles.find(r => !r.is_system && r.tenant_id
          // !== null) ?? allRoles[0]`, to "avoid a select-a-role empty state".
          // For a tenant with no custom roles that fell through to
          // allRoles[0], and GET /admin/roles sorts { is_system: -1, name: 1 },
          // so the first row is the alphabetically first SYSTEM role -
          // "Building Admin". Typing a name and an email and pressing Send
          // without touching the picker therefore created a third_party_admin
          // with zero locations and zero chargers: a colleague whose first
          // sign-in shows an admin app with nothing in it.
          //
          // An empty state the admin must resolve is the correct trade against
          // silently creating the wrong persona. The Send button is disabled
          // until they resolve it.
          setRoleId('');
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, userId]);

  // Resolve the currently-selected role's system_role so the
  // scope section's visibility tracks the role picker. Falls
  // back to null (no scope picker) if the role hasn't loaded
  // yet or the id doesn't match a known role.
  const selectedRole = useMemo(
    () => roles.find((r) => r._id === roleId),
    [roles, roleId],
  );
  const showScopePicker =
    mode === 'invite' && selectedRole?.system_role === 'third_party_admin';
  // Fleet Operators are scoped to fleets (AccessAssignment.fleet_ids), NOT
  // via the Access Management page (which holds station/charger scope). On
  // invite the fleets are picked right here (required); on edit, more fleets
  // are added from a fleet's Admins tab. Steer the helper text accordingly.
  const isFleetOperatorRole = selectedRole?.system_role === 'fleet_admin';
  const showFleetPicker = mode === 'invite' && isFleetOperatorRole;
  // Send stays disabled until a fleet is picked — mirrors the server's
  // FLEET_REQUIRED gate so the admin never sees it as an error.
  const fleetPickIncomplete = showFleetPicker && draftFleets.size === 0;
  // Send stays disabled until a role is picked. There is no default, so
  // roleId is '' until the admin chooses; without this the placeholder would
  // just move the same blind save one 400 further down.
  const rolePickIncomplete = mode === 'invite' && roleId === '';
  // Sales Rep and Sales Manager roles live on the Sales Staff page, which
  // keeps the role, the staff record and the rep's book together (Warren,
  // 2026-10-20). Here a sales staff member's role is read-only; the Api
  // refuses the change either way (SALES_STAFF_MANAGED).
  const salesManaged = mode === 'edit' && isSalesTemplate(detail?.role?.template);

  // Lazy-fetch the tenant's available locations + chargers the
  // FIRST time the scope picker becomes relevant. Subsequent
  // role changes within the same modal session reuse the fetched
  // data — no refetch on every role-picker tweak.
  useEffect(() => {
    if (!showScopePicker || scopeLoaded) return;
    let cancelled = false;
    setScopeLoading(true);
    void Promise.all([
      fetchLocations({ limit: 200 }),
      fetchChargers({ limit: 500 }),
    ])
      .then(([locsRes, chargersRes]) => {
        if (cancelled) return;
        setScopeLocations(
          locsRes.data.map((l) => ({ _id: l._id, name: l.name })),
        );
        setScopeChargers(
          chargersRes.data.map((c) => ({
            _id: c._id,
            identity: c.identity,
            location_id: c.location_id,
          })),
        );
        setScopeLoaded(true);
      })
      .catch(() => {
        // Soft-fail: the modal still works without scope; admin
        // can skip and assign later via /access.
        if (!cancelled) setScopeLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setScopeLoading(false);
      });
    return () => { cancelled = true; };
  }, [showScopePicker, scopeLoaded]);

  // Lazy-fetch the tenant's fleets the FIRST time the fleet picker becomes
  // relevant. GET /admin/fleets is already tenant-scoped server-side.
  useEffect(() => {
    if (!showFleetPicker || fleetsLoaded) return;
    let cancelled = false;
    setFleetsLoading(true);
    void fetchFleets()
      .then((rows) => {
        if (cancelled) return;
        // Only active fleets are offered — binding a new admin to a
        // deactivated fleet gives them nothing to manage.
        setFleets(rows.filter((f) => f.is_active));
        setFleetsLoaded(true);
      })
      .catch((err: unknown) => {
        // Unlike scope, the fleet pick is REQUIRED, so a failed load has to
        // surface: with no list there is nothing to pick and Send stays
        // disabled. Marked loaded so the effect does not spin on retry.
        if (cancelled) return;
        setFleetsLoaded(true);
        setError(err instanceof Error ? err.message : 'Failed to load fleets');
      })
      .finally(() => {
        if (!cancelled) setFleetsLoading(false);
      });
    return () => { cancelled = true; };
  }, [showFleetPicker, fleetsLoaded]);

  const toggleFleet = (id: string): void => {
    setDraftFleets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // "Create a fleet first" opens /fleets in a NEW tab so the half-typed
  // invite (name, email, role) survives; the admin creates the fleet
  // there, comes back, and refreshes the list. Closing the modal here
  // threw the draft away and left no way back.
  const refreshFleets = (): void => {
    setFleets([]);
    setFleetsLoaded(false);
  };

  const submit = async (): Promise<void> => {
    setError('');
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (mode === 'invite') {
      if (!email.trim()) {
        setError('Email is required');
        return;
      }
      if (!roleId) {
        setError('Pick a role for the new teammate');
        return;
      }
      // Defensive twin of the disabled Send button (keyboard submit, stale
      // render); the server enforces the same rule as FLEET_REQUIRED.
      if (fleetPickIncomplete) {
        setError('Pick at least one fleet for the new fleet admin');
        return;
      }
    }
    setSaving(true);
    try {
      if (mode === 'invite') {
        // Include scope payload only when the role honors it AND
        // the admin actually picked something. Empty selection
        // = unscoped invite + needs_scope notification fires
        // server-side, which is the documented "I'll scope them
        // later" path.
        const scope = showScopePicker
          && (draftLocations.size > 0 || draftChargers.size > 0)
          ? {
              location_ids: [...draftLocations],
              charger_ids: [...draftChargers],
            }
          : undefined;
        await inviteAdminUser({
          name: name.trim(),
          email: email.trim(),
          role_id: roleId,
          ...(scope ? { scope } : {}),
          // Fleet admins are grant-only server-side; the native flag is
          // rejected for them, so it is dropped here rather than sent.
          ...(native && !showFleetPicker ? { native: true } : {}),
          ...(showFleetPicker ? { fleet_ids: [...draftFleets] } : {}),
        });
      } else if (userId) {
        const body: { name?: string; role_id?: string; is_active?: boolean } = {};
        if (detail && name.trim() !== detail.name) body.name = name.trim();
        if (roleId && detail && roleId !== detail.role_id) body.role_id = roleId;
        if (detail && isActive !== detail.is_active) body.is_active = isActive;
        if (Object.keys(body).length === 0) {
          onClose();
          return;
        }
        await updateAdminUser(userId, body);
      }
      onSaved();
    } catch (err) {
      // Surface the server's copy verbatim (e.g. the last-active-admin 409 or a
      // tier-cap 403) rather than ky's generic "Request failed".
      setError(await getDisplayErrorMessage(err, 'Save failed'));
      setSaving(false);
    }
  };

  // Filter system role rows that aren't sensible to assign — the
  // global super_admin row is platform-level and never assigned via
  // the admin UI. Everything else surfaces:
  //   - Tenant-scoped system clones (four bootstrap roles) — direct.
  //   - Global third_party_admin templates (building_admin /
  //     charger_owner / property_manager) — direct, with the scope
  //     picker below auto-appearing on selection. The backend invite
  //     endpoint accepts globals and the scope payload covers the
  //     "where can they work?" half of the third_party_admin tier,
  //     so forcing a clone-first detour buys nothing for the common
  //     case.
  //   - Tenant-scoped custom clones (admin-created via Roles page) —
  //     direct, distinguished by is_system=false.
  const assignableRoles = roles.filter((r) => {
    if (r.system_role === 'super_admin' && r.tenant_id === null) return false;
    // Given on the Sales Staff page only; the Api refuses them here.
    if (isSalesTemplate(r.template)) return false;
    return true;
  });

  // ── The role picker speaks the persona's word, not ours ───────────
  //
  // Naming and grouping live in ./roleLabels.ts so the vocabulary rule is
  // testable on its own: no system word reaches the picker, and no role is
  // ever dropped from it. See roleLabels.test.ts.
  const { groups: grouped, ungrouped } = groupRoles(assignableRoles);

  const title = mode === 'invite'
    ? 'Invite teammate'
    : detail ? `Edit ${detail.name}` : 'Edit teammate';

  return (
    <Modal isOpen title={title} onClose={onClose} size="md">
      {loading ? (
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading…
        </div>
      ) : (
        <div className={styles.modalBody}>
          <div className={formStyles.field}>
            <label className={formStyles.label}>Name</label>
            <input
              className={formStyles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jamie Rivera"
              autoFocus={mode === 'invite'}
              disabled={saving}
            />
          </div>

          <div className={formStyles.field}>
            <label className={formStyles.label}>Email</label>
            <input
              className={formStyles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jamie@example.com"
              disabled={saving || mode === 'edit'}
            />
            {mode === 'edit' && (
              <p className={styles.modalHint}>
                Email is the account anchor and can&rsquo;t be changed here.
                If a teammate&rsquo;s address changes, deactivate this account
                and invite the new address.
              </p>
            )}
          </div>

          {salesManaged ? (
            <div className={formStyles.field}>
              <label className={formStyles.label}>Role</label>
              <input className={formStyles.input} value={roleNameForDisplay(detail?.role?.name, detail?.role?.template)} readOnly disabled />
              <p className={styles.modalHint}>
                This person is on the sales team. Their role is set on
                the <a href="/sales-staff">Sales Staff</a> page.
              </p>
            </div>
          ) : (
          <div className={formStyles.field}>
            <label className={formStyles.label}>Role</label>
            <select
              className={formStyles.select}
              value={roleId}
              onChange={(e) => {
                // A sales pick leaves roleId alone, so the picker snaps back
                // and nothing about it can reach Save.
                const pick = salesTeamPickFromValue(e.target.value);
                if (pick) { void handOffToSalesStaff(pick); return; }
                setSalesHandoff('');
                setRoleId(e.target.value);
              }}
              disabled={saving || salesChecking}
            >
              {assignableRoles.length === 0 ? (
                <option value="">(no roles available)</option>
              ) : (
                // No default. An invite with the role left alone used to save
                // whatever sorted first - alphabetically "Building Admin", a
                // scoped Site persona with no locations and no chargers, i.e.
                // an admin app that renders nothing. The placeholder makes the
                // pick deliberate; Send stays disabled until it is made.
                mode === 'invite' && <option value="">Choose a role…</option>
              )}
              {grouped.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.roles.map((r) => (
                    <option key={r._id} value={r._id}>{roleDisplayName(r)}</option>
                  ))}
                </optgroup>
              ))}
              {ungrouped.length > 0 && (
                <optgroup label="Other">
                  {ungrouped.map((r) => (
                    <option key={r._id} value={r._id}>{roleDisplayName(r)}</option>
                  ))}
                </optgroup>
              )}
              {isSuperAdmin && (
                <optgroup label="Sales team">
                  {SALES_TEAM_PICKS.map((p) => (
                    <option key={p.pick} value={salesTeamOptionValue(p.pick)}>{p.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {isSuperAdmin && (
              <p className={styles.modalHint}>
                Sales team roles are recorded on the Sales Staff page, with the
                manager and the start date. Choosing one saves nothing here: it
                opens that page&rsquo;s add form for this person. A new email gets
                the normal account invite, and the record is created when they
                accept.
              </p>
            )}
            {salesHandoff && <div className={styles.error} role="alert">{salesHandoff}</div>}
            <p className={styles.modalHint}>
              {showFleetPicker ? (
                <>
                  Roles drive what the teammate can do. A fleet admin is
                  scoped to specific fleets — pick theirs below. More fleets
                  can be added later from a fleet&rsquo;s <strong>Admins</strong>{' '}
                  tab. (Access Management covers station + charger scope, not
                  fleets.)
                </>
              ) : isFleetOperatorRole ? (
                <>
                  Roles drive what the teammate can do. A fleet admin is
                  scoped to specific fleets — grant fleet access from a
                  fleet&rsquo;s <strong>Admins</strong> tab after saving.
                  (Access Management covers station + charger scope, not
                  fleets.)
                </>
              ) : (
                <>
                  Roles drive what the teammate can do. After saving, fine-tune
                  their station + charger scope on the Access Management page.
                </>
              )}
            </p>
          </div>
          )}

          {/* Native-admin escape hatch — invite mode, super_admin only.
              Default (unchecked) is the grant path: the new admin also holds
              a driver identity and can sign into the driver app. Hidden for
              fleet_admin, which is grant-only (the server rejects native). */}
          {mode === 'invite' && isSuperAdmin && !showFleetPicker && (
            <div className={formStyles.field}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={native}
                  onChange={(e) => setNative(e.target.checked)}
                  disabled={saving}
                />
                <span className={formStyles.label} style={{ margin: 0 }}>
                  Native admin account (no driver identity)
                </span>
              </label>
              <p className={styles.modalHint}>
                By default a new admin also gets a driver account and signs into
                the driver app with the same email. Check this to create a
                back-office-only admin with no driver identity.
              </p>
            </div>
          )}

          {/* Scope picker — visible only when inviting a
              third_party_admin role. Other role tiers either bypass
              scope (super_admin / tenant_admin) or operate
              tenant-wide (operator / viewer), so the section stays
              hidden for them. Picker is OPTIONAL — admin can skip
              and the backend's needs_scope notification fires
              server-side as a backstop. */}
          {showScopePicker && (
            <div className={formStyles.field}>
              <label className={formStyles.label}>Where can they work?</label>
              <p className={styles.modalHint} style={{ marginBottom: 8 }}>
                Optional. Without scope, they have a role but no
                buildings. You can assign scope now or later from
                Access Management.
              </p>
              {scopeLoading ? (
                <div className={styles.loading}>
                  <Loader2 size={14} className={styles.spin} /> Loading locations…
                </div>
              ) : (
                <ScopePicker
                  availableLocations={scopeLocations}
                  availableChargers={scopeChargers}
                  selectedLocations={draftLocations}
                  selectedChargers={draftChargers}
                  onLocationsChange={setDraftLocations}
                  onChargersChange={setDraftChargers}
                  disabled={saving}
                />
              )}
            </div>
          )}

          {/* Fleet picker — visible only when inviting a fleet_admin role.
              REQUIRED (unlike scope): the backend refuses a fleet admin
              with no fleet, because AccessAssignment.fleet_ids is the only
              thing their Fleets list and every fleet route read. */}
          {showFleetPicker && (
            <div className={formStyles.field}>
              <label className={formStyles.label}>Which fleets do they manage?</label>
              <p className={styles.modalHint} style={{ marginBottom: 8 }}>
                Required. Pick at least one fleet. They will see only these
                fleets in the admin.
              </p>
              {fleetsLoading ? (
                <div className={styles.loading}>
                  <Loader2 size={14} className={styles.spin} /> Loading fleets…
                </div>
              ) : fleets.length === 0 ? (
                <p className={styles.modalHint}>
                  No fleets yet.{' '}
                  <a href="/fleets" target="_blank" rel="noopener noreferrer">Create a fleet first</a>
                  {' '}(opens in a new tab), then{' '}
                  <button
                    type="button"
                    onClick={refreshFleets}
                    style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                  >
                    refresh this list
                  </button>
                  {' '}to pick it.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {fleets.map((f) => (
                    <label
                      key={f._id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={draftFleets.has(f._id)}
                        onChange={() => toggleFleet(f._id)}
                        disabled={saving}
                      />
                      <span>{f.name}</span>
                    </label>
                  ))}
                  <p className={styles.modalHint} style={{ margin: '4px 0 0' }}>
                    {draftFleets.size} of {fleets.length} selected
                  </p>
                </div>
              )}
            </div>
          )}

          {mode === 'edit' && detail && (
            <div className={formStyles.field}>
              <label className={formStyles.label}>Status</label>
              <div className={styles.fieldRow}>
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={saving}
                  label="Active"
                />
                <span className={`${styles.stateBadge} ${STATE_CLASSES[detail.state]}`}>
                  {STATE_LABELS[detail.state]}
                </span>
              </div>
              {!isActive && detail.is_active && (
                <div className={styles.modalDangerNote}>
                  Deactivating revokes this teammate&rsquo;s sign-in access
                  immediately. Their data is preserved — flip Active back on
                  at any time to restore access.
                </div>
              )}
            </div>
          )}

          {/* MFA status — detail-only readout. The list row carries
              the boolean badge; here we also surface the enrolled-at
              date from the detail fetch. The reset control lives on
              the list row, not here. */}
          {mode === 'edit' && detail && (
            <div className={formStyles.field}>
              <label className={formStyles.label}>Two-factor authentication</label>
              <div className={styles.fieldRow}>
                <span
                  className={`${styles.mfaBadge} ${detail.mfa_enabled ? styles.mfaOn : styles.mfaOff}`}
                >
                  {detail.mfa_enabled ? '2FA on' : '2FA off'}
                </span>
                {detail.mfa_enabled && detail.mfa_enrolled_at && (
                  <span className={styles.rowAside}>
                    Enrolled {new Date(detail.mfa_enrolled_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.modalActions}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={() => void submit()}
              disabled={saving || fleetPickIncomplete || rolePickIncomplete}
            >
              {saving
                ? <><Loader2 size={13} className={styles.spin} /> Saving…</>
                : mode === 'invite'
                  ? <><UserPlus size={13} /> Send invite</>
                  : <><UserCog size={13} /> Save changes</>}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = then.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const minutes = Math.floor(absMs / (60 * 1000));
  const past = diffMs < 0;
  const fmt = (n: number, unit: string): string =>
    past ? `${n} ${unit}${n === 1 ? '' : 's'} ago` : `in ${n} ${unit}${n === 1 ? '' : 's'}`;
  if (minutes < 1) return past ? 'just now' : 'soon';
  if (minutes < 60) return fmt(minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return fmt(hours, 'hour');
  const days = Math.floor(hours / 24);
  return fmt(days, 'day');
}
